import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, X, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { useAiChat } from '@/hooks/useAiChat.js';
import { canAccessIa, type AcademyRecursos } from '@shared/entitlements.js';

type ChatAssistFabProps = {
  recursos?: AcademyRecursos | null;
};

export function ChatAssistFab({ recursos }: ChatAssistFabProps) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const chat = useAiChat('CHAT_ALUNO');

  const iaOk = recursos ? canAccessIa(recursos) : true;

  useEffect(() => {
    if (loc.pathname === '/tecnologia/hack-ia' || loc.pathname === '/assistente') setOpen(false);
  }, [loc.pathname]);

  if (!iaOk || loc.pathname === '/tecnologia/hack-ia' || loc.pathname === '/assistente') return null;

  return (
    <>
      <button
        type="button"
        aria-label="Abrir assistente IA"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-black transition hover:scale-105"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Bot className="h-6 w-6" strokeWidth={1.5} />}
      </button>

      {open ? (
        <div
          className="fixed bottom-24 right-6 z-50 flex w-[min(100vw-2rem,380px)] flex-col overflow-hidden rounded-card border border-ha-border2 bg-ha-card"
          role="dialog"
          aria-label="Hack Growth IA"
        >
          <div className="flex items-center justify-between border-b border-ha-border2 px-4 py-3">
            <span className="text-sm font-semibold text-ha-text">Hack Growth IA</span>
            <Link
              to="/tecnologia/hack-ia"
              className="flex items-center gap-1 text-xs text-ha-muted hover:text-accent"
              title="Abrir página completa"
            >
              <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              Expandir
            </Link>
          </div>
          <div className="flex max-h-64 min-h-[200px] flex-col overflow-y-auto p-3">
            {chat.messages.length === 0 ? (
              <p className="text-xs text-ha-muted">Pergunte sobre o método ou seu diagnóstico.</p>
            ) : null}
            {chat.messages.map((m, i) => (
              <p
                key={i}
                className={cn(
                  'mb-2 text-xs leading-relaxed',
                  m.role === 'user' ? 'text-ha-text' : 'text-ha-subtle',
                )}
              >
                <strong>{m.role === 'user' ? 'Você' : 'Hack Growth IA'}:</strong> {m.content}
              </p>
            ))}
            {chat.error ? <p className="text-xs text-feedback">{chat.error}</p> : null}
          </div>
          <form
            className="flex gap-2 border-t border-ha-border2 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              chat.submit();
            }}
          >
            <input
              className="min-w-0 flex-1 rounded-element border border-ha-border2 bg-ha-card2 px-2 py-2 text-xs"
              value={chat.input}
              onChange={(e) => chat.setInput(e.target.value)}
              placeholder="Sua pergunta…"
            />
            <button
              type="submit"
              disabled={chat.send.isPending}
              className="rounded-element bg-accent px-3 py-2 text-[10px] font-bold uppercase text-black disabled:opacity-50"
            >
              OK
            </button>
          </form>
        </div>
      ) : null}
    </>
  );
}
